
import { ServiceLandingPage } from "@/components/service/ServiceLandingPage";
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { ContractorsList } from "@/components/ContractorsList";
import { LoadingState } from "@/components/LoadingState";
import { useState } from "react";

const PaintingPage = () => {
  const serviceType = "Painting";
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  
  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'painting'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'Painting')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const features = [
    {
      title: "Interior Painting",
      description: "Professional painting for all indoor spaces"
    },
    {
      title: "Exterior Painting",
      description: "Weather-resistant painting for home exteriors"
    },
    {
      title: "Cabinet Refinishing",
      description: "Transform your kitchen or bathroom with cabinet refinishing"
    },
    {
      title: "Color Consultation",
      description: "Expert advice on color selection and design"
    },
    {
      title: "Deck & Fence Staining",
      description: "Protect and beautify your outdoor wooden surfaces"
    }
  ];

  const processSteps = [
    {
      title: "Free Consultation",
      description: "Discuss your vision and receive expert recommendations"
    },
    {
      title: "Detailed Quote",
      description: "Comprehensive estimate with no hidden costs"
    },
    {
      title: "Professional Preparation",
      description: "Thorough surface preparation for lasting results"
    },
    {
      title: "Quality Application",
      description: "Skilled painters using premium materials"
    },
    {
      title: "Final Walkthrough",
      description: "Inspection to ensure your complete satisfaction"
    }
  ];

  const benefits = [
    {
      title: "Licensed & Insured",
      description: "Professional protection for your property"
    },
    {
      title: "Quality Materials",
      description: "Premium paints and finishes for durability"
    },
    {
      title: "Clean Workspaces",
      description: "We protect your space and clean up thoroughly"
    },
    {
      title: "Flexible Scheduling",
      description: "Work planned around your convenience"
    },
    {
      title: "Warranty Protection",
      description: "Our work is backed by a satisfaction guarantee"
    }
  ];

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "Painting", href: "/painting-contractors-oklahoma-city" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Painting Services in Oklahoma City",
    "description": "Professional painting services in Oklahoma City for residential and commercial properties.",
    "provider": {
      "@type": "Organization",
      "name": "Home Pros OKC"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oklahoma City"
    },
    "serviceType": "Painting"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Painting Services in Oklahoma City | Home Pros OKC"
        description="Professional painting services in Oklahoma City for residential and commercial properties."
        canonicalUrl="/painting-contractors-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title="Painting Services in Oklahoma City"
          subtitle="Transform your space with Oklahoma City's top-rated painting contractors."
          buttonText="Get a Free Quote"
          buttonLink="#quote-form"
        />

        <ServiceLandingPage
          title="Painting Services in Oklahoma City"
          metaDescription="Professional painting services in Oklahoma City for residential and commercial properties."
          introText="Transform your space with Oklahoma City's top-rated painting contractors."
          features={features}
          processSteps={processSteps}
          benefits={benefits}
          ctaText="Ready to refresh your space?"
          ctaButtonText="Get Connected"
          contactText="Fill out the form below to connect with top-rated painters in OKC."
        />

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top Painting Professionals in Oklahoma City</h2>
          
          {isLoading ? (
            <LoadingState message="Loading painting professionals..." />
          ) : contractors && contractors.length > 0 ? (
            <ContractorsList
              contractors={contractors}
              isLoading={isLoading}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onServiceTypeChange={() => {}}
              onLocationChange={() => {}}
              selectedServiceType="Painting"
            />
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of painting professionals in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-4">Looking for Our Top-Rated Painting Services?</h3>
          <p className="mb-6">
            View our curated list of the best painting contractors in Oklahoma City.
          </p>
          <a 
            href="/best-painting-contractors-okc" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition"
          >
            See Top-Rated Painting Contractors
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaintingPage;
